import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * The app's message box: one dialog for every confirmation and decision.
 *
 * Replaces `window.confirm`, which the browser draws in its own chrome. That
 * has three costs beyond looking wrong: it cannot be styled, it blocks the
 * whole tab while it is up, and on a destructive action it gives "OK" and
 * "Cancel" instead of naming what is about to happen. A person clicking
 * through a delete should read the word "Delete", not "OK".
 *
 * Used imperatively, so a call site reads almost the way the old one did:
 *
 *     const confirm = useConfirm();
 *     if (!(await confirm({ title: 'Delete this row?', tone: 'danger' }))) return;
 *
 * One instance is mounted at the app root, so nothing else has to hold dialog
 * state or remember to render it.
 */

export type ConfirmTone = 'default' | 'danger';

export type ConfirmRequest = {
  /** The decision, as a question. */
  title: string;
  /** What will happen. Optional, but usually the reason to stop and read. */
  message?: React.ReactNode;
  /** Names the action, never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
};

/**
 * The shape a screen-level adapter over {@link useConfirm} should accept.
 *
 * `tone` and `confirmLabel` are REQUIRED here, unlike on ConfirmRequest. An
 * adapter that defaults them has to pick a default for every future call site
 * sight unseen, and whichever it picks is wrong for some of them silently --
 * AdminHome defaulted to `danger` and so offered a red "Delete" for an action
 * that sends an email. Requiring both makes the compiler ask the author what
 * the action actually is, at the one moment they know the answer.
 *
 * The dialog's own defaults stay as they are: a call that reaches
 * ConfirmRequest directly still gets 'Delete' for danger and 'Continue'
 * otherwise, which is right for a hand-written call and wrong only for a
 * pass-through adapter.
 */
export type ConfirmIntent = {
  title?: string;
  message: string;
  /** Names the action: 'Delete', 'Remove', 'Realign', 'Send request'. */
  confirmLabel: string;
  /** 'danger' only when data is destroyed or overwritten. */
  tone: ConfirmTone;
};

type PendingRequest = ConfirmRequest & { resolve: (answer: boolean) => void };

const ConfirmContext = createContext<((request: ConfirmRequest) => Promise<boolean>) | null>(null);

/** Ask the user to confirm. Resolves true only if they choose the action. */
export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (confirm === null) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>.');
  }
  return confirm;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingRequest | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...request, resolve });
      }),
    [],
  );

  const settle = useCallback((answer: boolean) => {
    setPending((current) => {
      current?.resolve(answer);
      return null;
    });
  }, []);

  // Escape cancels and Enter confirms, the way the native dialog behaved, so
  // muscle memory still works.
  useEffect(() => {
    if (pending === null) return;
    confirmButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); settle(false); }
      if (event.key === 'Enter') { event.preventDefault(); settle(true); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pending, settle]);

  const danger = pending?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending !== null && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0b223a66] px-4 py-8"
          /* A click outside cancels: the safe answer, whatever the tone. */
          onClick={() => settle(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-[#1e6bb824] bg-white shadow-[0_20px_50px_rgba(30,107,184,0.18)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 pb-4 pt-5">
              <h2 id="confirm-dialog-title" className="text-lg font-semibold leading-snug tracking-tight text-avcs-text">
                {pending.title}
              </h2>
              {pending.message && (
                <div className="mt-2 text-sm leading-relaxed text-avcs-muted">{pending.message}</div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-[#1e6bb814] bg-[#fbfeff] px-5 py-3">
              <button
                type="button"
                className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2 text-sm font-semibold text-avcs-text transition-colors hover:bg-[#f1f7ff]"
                onClick={() => settle(false)}
              >
                {pending.cancelLabel || 'Cancel'}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                className={`rounded-lg px-3 py-2 text-sm font-semibold text-white transition-colors ${
                  danger ? 'bg-[#a11f1f] hover:bg-[#8c1a1a]' : 'bg-[#1e6bb8] hover:bg-[#1a5c9f]'
                }`}
                onClick={() => settle(true)}
              >
                {/* Never "OK": the button says what it does. */}
                {pending.confirmLabel || (danger ? 'Delete' : 'Continue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
